import { useCallback } from 'react'

import { TAgeGroup, TGender } from '~/entities'
import { getPartnerAPIKey } from '~/utils/getPartnerAPIKey'

import { LOCAL_STORAGE_MODELS_KEY, LOCAL_STORAGE_MODELS_KEY_V2 } from './constants'
import { ICurrentModel, IUseCurrentModel, TDefaultModels, TStorage } from './types'

export const useCurrentModel = (): IUseCurrentModel => {
  const deleteCurrentModel = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_MODELS_KEY)
  }, [])

  const getStorage = useCallback((): TStorage | undefined => {
    const storage = localStorage.getItem(LOCAL_STORAGE_MODELS_KEY)

    return storage && JSON.parse(storage)
  }, [])

  const getCurrentModel = useCallback(
    (): ICurrentModel => (getStorage()?.current && getStorage()?.current[getPartnerAPIKey()]) as ICurrentModel,
    [getStorage],
  )

  const setCurrentModel = useCallback(
    (item: ICurrentModel) => {
      const modelsStorage = getStorage() || { current: null }

      modelsStorage.current = {
        ...modelsStorage.current,
        [getPartnerAPIKey()]: item,
      }

      localStorage.setItem(LOCAL_STORAGE_MODELS_KEY, JSON.stringify(modelsStorage))
    },
    [getStorage],
  )

  const setDefaultModels = useCallback(
    (items: Array<ICurrentModel>) => {
      const modelsStorage = getStorage() || ({ default: {} } as TStorage)

      items?.forEach(item => {
        modelsStorage.default = {
          ...modelsStorage?.default,
          [getPartnerAPIKey()]: {
            ...(modelsStorage?.default && modelsStorage?.default[getPartnerAPIKey()]),
            [item?.gender?.toLowerCase() as string]: item,
          },
        }
      })

      localStorage.setItem(LOCAL_STORAGE_MODELS_KEY, JSON.stringify(modelsStorage))
    },
    [getStorage],
  )

  const setDefaultModelsAsArray = (items: Array<ICurrentModel>) => {
    const partnerAPIKey = getPartnerAPIKey()
    const payload = { default: { [partnerAPIKey]: items } }

    localStorage.setItem(LOCAL_STORAGE_MODELS_KEY_V2, JSON.stringify(payload))
  }

  const getDefaultModels = useCallback((): TDefaultModels => {
    const storage = getStorage()

    return storage?.default && storage?.default[getPartnerAPIKey()]
  }, [getStorage])

  const getDefaultModel = ({ gender, age_group }: { gender: string; age_group: string }): ICurrentModel | undefined => {
    const storage = localStorage.getItem(LOCAL_STORAGE_MODELS_KEY_V2)
    const partnerAPIKey = getPartnerAPIKey()

    if (!storage) return undefined

    const currentDefaultAvatars = JSON.parse(storage).default[partnerAPIKey]

    return currentDefaultAvatars.find(
      (preModel: ICurrentModel) => preModel.gender === gender && preModel.age_group === age_group,
    )
  }

  const getDefaultFallbackModel = (): ICurrentModel | null => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const storage = localStorage.getItem(LOCAL_STORAGE_MODELS_KEY_V2)!
    const partnerAPIKey = getPartnerAPIKey()

    const currentDefaultAvatars: ICurrentModel[] = JSON.parse(storage).default[partnerAPIKey]

    const priorityOrder: Array<{ gender: TGender; age_group: TAgeGroup }> = [
      { gender: 'MALE', age_group: 'ADULTS' },
      { gender: 'FEMALE', age_group: 'ADULTS' },
      { gender: 'MALE', age_group: 'CHILDREN' },
      { gender: 'FEMALE', age_group: 'CHILDREN' },
    ]

    for (const { gender, age_group } of priorityOrder) {
      const foundModel = currentDefaultAvatars.find(
        preModel => preModel.gender === gender && preModel.age_group === age_group,
      )
      if (foundModel) {
        return foundModel
      }
    }

    return null
  }

  return {
    getCurrentModel,
    setCurrentModel,
    setDefaultModels,
    getDefaultModels,
    deleteCurrentModel,
    setDefaultModelsAsArray,
    getDefaultModel,
    getDefaultFallbackModel,
  }
}
